import { forwardRef, RefObject, useRef, useState } from 'react'
import { ReactSVG } from 'react-svg'
import { useOnClickOutside } from 'usehooks-ts'

import { cn } from '@shared/lib/cn'
import Input, { InputProps } from '@shared/ui/input'

import styles from './MultiSelect.module.scss'


interface Option {
  name: string;
  value: string;
}

interface MultiSelectProps extends Omit<InputProps, 'value' | 'onChange'> {
  options: Option[];
  value?: string[]; // Массив выбранных значений
  onChange?: (values: string[]) => void;
  maxDisplayItems?: number; // Сколько элементов показывать в поле
}

const MultiSelect = forwardRef<HTMLInputElement, MultiSelectProps>((props, ref) => {
  const {
    options,
    inputSize = 'medium',
    value: selectedValues = [],
    onChange,
    maxDisplayItems = 3,
    placeholder,
    ...rest
  } = props

  const [ showOptions, setShowOptions ] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useOnClickOutside(rootRef as RefObject<HTMLDivElement>, () => {
    setShowOptions(false)
  })

  const handleToggle = (optionValue: string) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [ ...selectedValues, optionValue ]

    onChange?.(newValues)
  }

  const handleClear = () => {
    onChange?.([])
  }

  // Формируем отображаемый текст
  const getDisplayText = () => {
    if (selectedValues.length === 0) return ''

    const selectedOptions = options.filter((o) => selectedValues.includes(o.value))
    const names = selectedOptions.map((o) => o.name)

    if (names.length <= maxDisplayItems) {
      return names.join(', ')
    }

    return `${names.slice(0, maxDisplayItems).join(', ')}... (+${names.length - maxDisplayItems})`
  }

  return (
    <div
      ref={rootRef}
      className={cn(styles.root, {
        [styles[inputSize]]: inputSize,
        [styles.open]: showOptions,
      })}
    >
      <Input
        inputSize={inputSize}
        value={getDisplayText()}
        placeholder={placeholder}
        onMouseDown={(e) => {
          e.preventDefault()
          setShowOptions((prev) => !prev)
        }}
        onCustomClear={selectedValues.length > 0 ? handleClear : undefined}
        ref={ref}
        readOnly
        endDecorator={
          <ReactSVG
            className={styles.root__icon}
            src="/icons/arrow.svg"
            wrapper="div"
          />
        }
        {...rest}
      />

      {showOptions && (
        <div className={styles.root__options}>
          {options.map((item) => {
            const isSelected = selectedValues.includes(item.value)
            return (
              <label
                key={item.value}
                className={cn(styles.root__option, {
                  [styles.active]: isSelected,
                })}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggle(item.value)}
                  className={styles.root__checkbox}
                />
                <span>{item.name}</span>
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
})

MultiSelect.displayName = 'MultiSelect'

export default MultiSelect
